'use client'
import { useChannelWithFallback } from '@/api/hooks'
import { NewUserCallout } from '@/app/components/collections/callouts'
import ModalManager from '@/app/components/collections/modal-manager'
import LockIcon from '@/app/icons/lock'
import { getChannelClipsByUsername, getChannelGifsById } from '@/app/util/channel-api'
import Button from '@/components/button'
import StoriesCarousel from '@/components/carousel/stories-carousel'
import VideoCarousel from '@/components/carousel/video-carousel'
import ChannelGrid from '@/components/grid/channel-grid'
import Grid, { GridContext } from '@/components/grid/grid'
import { SectionHeader } from '@/components/sections'
import Username from '@/components/username'
import UAParserContext from '@/context/ua-parser'
import UserContext from '@/context/user-context'
import { NexaBlack } from '@/styles/fonts'
import { cn } from '@/util/cn'
import { IChannel, IGif } from '@giphy/js-types'
import { useCallback, useContext, useState } from 'react'
import { IStory, StoryResult } from 'types'
import { UserAnalytics } from './analytics-api'
import { useChannelChildren } from './channel-children'
import Banner from './components/banner'
import ChannelInfo from './components/channel-info'
import { EditCollectionButton, NewCollectionButton, ShareCollectionButton } from './components/page-header'
import { containerCss, desktopContainerCss } from './constants'
import ChannelDangerZone from './components/channel-danger-zone'

// fetch the first few gifs on the server
const clientsideFetchLimit = 25

type Props = {
    channel: IChannel
    initialGifs?: IGif[]
    initialClips?: IGif[]
    analytics?: UserAnalytics
    storyData?: StoryResult
}

const ChannelPageContents = ({ channel: _channel, analytics, initialGifs = [], initialClips, storyData }: Props) => {
    const { user } = useContext(UserContext)
    const { data: channel = _channel } = useChannelWithFallback(_channel)
    const children = useChannelChildren(channel.id, channel.children)
    const { deviceType } = useContext(UAParserContext)
    const {
        id,
        user: { username, display_name },
    } = channel

    const fetchClips = useCallback(
        async (offset: number) => {
            return await getChannelClipsByUsername(
                username,
                { offset, limit: clientsideFetchLimit },
                document.cookie as string
            )
        },
        [username]
    )
    const fetchGifs = useCallback(
        async (offset: number) => {
            return await getChannelGifsById(id, { offset, limit: clientsideFetchLimit }, document.cookie)
        },
        [id]
    )
    const isOwnChannel = user && (user.is_staff || user.username === channel.user.username)
    // refreshes the main grid but not the pagination carousles when using Add To Collection from the carousel
    const [externalGifs, setExternalGifs] = useState<IGif[]>(initialGifs)

    const kidCount = children?.length || 0
    const hasCollections = kidCount > 0
    const hasGifs = !!externalGifs?.length
    const hasClips = !!initialClips?.length
    const hasStories = !!storyData?.data?.length
    const isEmpty = !hasGifs && !hasClips && !hasStories && !hasCollections
    return (
        <GridContext.Provider value={{ gifs: externalGifs, setGifs: setExternalGifs }}>
            <ModalManager channel={channel}>
                <Banner channel={channel} />
                <div className={cn(containerCss, desktopContainerCss)}>
                    <ChannelInfo channel={channel} analytics={analytics} display_name={display_name} />
                    <div className={cn(containerCss, 'flex-1')}>
                        <section className="hidden justify-between md:flex">
                            {/* make room for deactivate button for staff */}
                            <div className={cn('flex max-w-[420px] flex-col', user?.is_staff && `max-w-[360px]`)}>
                                <h1
                                    className={`${NexaBlack.className} flex shrink items-center justify-center gap-2 text-3xl select-none`}
                                >
                                    {channel.user.display_name || channel.user.username}
                                </h1>
                                <Username
                                    user={channel.user}
                                    className="text-giphyBlue hover:underline [&_svg]:fill-giphyBlue"
                                />
                            </div>
                            {channel.is_private && <LockIcon className="mx-4 size-8 shrink-0 fill-giphyRed" />}
                            <div className="flex shrink-0 gap-2">
                                <ShareCollectionButton channel={channel} />
                                {user?.is_staff && <ChannelDangerZone channel={channel} />}
                                {isOwnChannel && (
                                    <Button className="h-10 bg-gradient-grey" href="/settings" isExternal>
                                        Edit Channel
                                    </Button>
                                )}
                                {channel?.children?.length === 0 ? (
                                    <NewCollectionButton channel={channel} />
                                ) : (
                                    <EditCollectionButton channel={channel} />
                                )}
                            </div>
                        </section>
                        {isEmpty ? (
                            isOwnChannel ? (
                                <NewUserCallout channel={channel} />
                            ) : (
                                <h2 className="grid place-items-center">
                                    This user hasn&apos;t created any content yet.
                                </h2>
                            )
                        ) : (
                            <>
                                {storyData?.data && storyData?.data.length > 0 && (
                                    <section>
                                        <StoriesCarousel
                                            user={channel.user}
                                            initialData={storyData.data}
                                            initialUrl={storyData.pagination.next_page}
                                            onClick={(story: IStory) => {
                                                location.href = `/stories/${story.slug}`
                                            }}
                                        />
                                    </section>
                                )}
                                {channel.has_clips_feed && (
                                    <section>
                                        <VideoCarousel
                                            link={`/${username}/clips`}
                                            initialGifs={initialClips}
                                            fetchGifs={fetchClips}
                                            hideUsername
                                        />
                                    </section>
                                )}
                                {hasCollections && (
                                    <section>
                                        <h4 className="mx-1 my-2 select-none md:mx-0">{kidCount} Collections</h4>
                                        <ChannelGrid key={`ChannelGrid-${channel.id}`} channel={channel} />
                                    </section>
                                )}
                                <section>
                                    <SectionHeader label={`All the ${channel.user.display_name} GIFs`} />
                                    <Grid
                                        columns={deviceType === 'desktop' ? 3 : undefined}
                                        initialGifs={initialGifs}
                                        fetchGifs={fetchGifs}
                                        key={`all-gifs-${channel.id}`}
                                        width={deviceType === 'desktop' ? 776 : undefined}
                                    />
                                </section>
                            </>
                        )}
                    </div>
                </div>
            </ModalManager>
        </GridContext.Provider>
    )
}

export default ChannelPageContents
